import Vue from 'vue';
import VueI18n from 'vue-i18n';
import PortalVue from 'portal-vue';
import debounce from 'lodash-es/debounce';

import 'intersection-observer'; // Needed for Safari
import VueObserveVisibility from 'vue-observe-visibility';

import VueAxios from 'vue-axios';
import Vuelidate from 'vuelidate';
import axios from 'axios';

import DebugPlugin from '@/modules/debug/debug-plugin';
import ModalPlugin from '@/plugins/modal-helper';
import ComponentTranslationsPlugin from '@/plugins/component-translations';

import App from './App';
import router from './router';
import * as svgicon from 'vue-svgicon';
import VueLazyload from 'vue-lazyload';
import { abilitiesPlugin } from '@casl/vue';
import ability, { updateAbility } from '@/ability';

// import VueAnalytics from 'vue-analytics'
import * as Sentry from '@sentry/browser';

import store from './store';
import { SET_LOCALE } from './store/mutation-types';

import messages from './locales';
import localsPreprocess from './locales-preprocess';
import moment from 'moment';
import 'moment/locale/nb';

import IntercomPlugin from './intercom';
import pageVisibility from './page-visibility';
import settings from './settings';
import { EventBus } from '@/event-bus';

// NOTE: https://github.com/aurelia/binding/issues/376
import { initialize as initializePal } from 'aurelia-pal-browser';

import smoothscroll from 'smoothscroll-polyfill';
import Vue2TouchEvents from 'vue2-touch-events';

export default async function () {
    initializePal();

    // kick off the polyfill!
    smoothscroll.polyfill();

    Vue.config.productionTip = false;

    Vue.config.ignoredElements = [/^my-/, 'memolanguage-root'];

    Vue.use(VueAxios, axios);
    Vue.use(ComponentTranslationsPlugin);
    Vue.use(VueI18n);
    Vue.use(PortalVue);
    Vue.use(Vuelidate);
    Vue.use(svgicon, {
        tagName: 'svgicon'
    });

    Vue.use(VueLazyload);
    Vue.use(VueObserveVisibility);
    Vue.use(abilitiesPlugin, ability);
    Vue.use(DebugPlugin);
    Vue.use(ModalPlugin);
    Vue.use(IntercomPlugin);
    Vue.use(Vue2TouchEvents);

    store.watch(
        state => state.moduleAuth.profile,
        val => {
            updateAbility(store.getters['moduleAuth/profile']);
        },
        { deep: true }
    );
    store.watch(
        state => state.moduleApp.lifeskills,
        val => {
            updateAbility(store.getters['moduleAuth/profile']);
        },
        { deep: true }
    );

    window.dbgToggleDebugButton = function () {
        store.dispatch('moduleApp/toggleDebugButton');
    };

    const i18n = new VueI18n({
        // Note: locale set below, in updateLocal
        silentTranslationWarn: true,
        messages: localsPreprocess(messages)
    });

    function updateLocal(language) {
        i18n.locale = language;
        moment.locale(language);
    }

    await store.dispatch('moduleApp/initApp');

    if (store.state.moduleAuth.isAuthenticated) {
        const lang = store.state.moduleAuth.profile.language || 'no';
        store.commit(`moduleApp/${SET_LOCALE}`, lang);
        updateLocal(lang);
    } else {
        // NOTE: appLanguage initially loaded (syncronously) in persistence plugin. If it is blank, it probably means that this is the first time the user loads that app
        if (!store.state.moduleApp.memolife.appLanguage) {
            let suggestedLang = 'no'; // NOTE: default language
            try {
                const geoResponse = await axios.get(`${settings.endpoints.api}/geo/country/`);
                if (geoResponse.data.country_code !== 'NO') {
                    suggestedLang = 'en';
                }
            } catch (err) {
                Sentry.captureException(err);
            }
            store.commit(`moduleApp/${SET_LOCALE}`, suggestedLang);
        }
        updateLocal(store.state.moduleApp.memolife.appLanguage);
    }

    store.watch(
        state => store.getters['moduleApp/locale'],
        val => {
            updateLocal(val);
        }
    );
    store.watch(
        state => state.moduleAuth.profile,
        profile => {
            Sentry.configureScope(scope => {
                if (profile) {
                    scope.setUser({
                        email: profile.email,
                        id: profile._id,
                        username: profile.name
                    });
                } else {
                    scope.setUser({});
                }
            });
        },
        { immediate: true, deep: true }
    );

    store.watch(
        state => store.getters['moduleApp/gettingStartedTotalProgress'],
        progress => {
            window.dataLayer.push({ event: 'getting_started_item_done', progress: progress });
        },
        { immediate: false, deep: false }
    );

    router.beforeEach((to, from, next) => {
        store.commit('routerStartNavigation');
        const isAuthenticated = store.state.moduleAuth.isAuthenticated;

        // NOTE: Since most routes require auth, meta.requiresAuth as undefined equals true
        if (to.matched.some(record => record.meta.requiresAuth !== false)) {
            if (!isAuthenticated) {
                next({
                    path: '/login',
                    query: { next: to.fullPath }
                });
            } else {
                next();
            }
        } else {
            next();
        }
    });

    router.afterEach((to, from) => {
        store.commit('routerEndNavigation');
    });
    router.onError(function () {
        store.commit('routerEndNavigation');
        // NOTE: If already on /home, nothing will happen here
        router.push({ name: 'DashboardHomeView' });
    });
    router.onReady(function () {});

    if (process.env.NODE_ENV === 'production') {
        if (location.hash) {
            history.replaceState(null, null, location.hash.substr(1) + location.search);
        }
    }

    await store.dispatch('moduleApp/activateApp');

    if (store.state.moduleAuth.isAuthenticated) {
        await store.dispatch('moduleApp/loadUser');
    }
    // updateAbility(store.state.moduleAuth.profile)

    pageVisibility.subscribe(state => {
        if (state === 'visible') {
            store.dispatch('moduleApp/activateApp');
        } else {
            store.dispatch('moduleApp/deactivateApp');
        }
    });

    function updateViewHeightVariable(x) {
        const vh = window.innerHeight * 0.01;
        document.documentElement.style.setProperty('--vh', `${vh}px`);
    }

    let resizing = false;
    function onResize() {
        resizing = !resizing;
        if (resizing) {
            document.body.classList.add('is-resizing');
        } else {
            document.body.classList.remove('is-resizing');
            updateViewHeightVariable();
            EventBus.$emit('resize-end');
        }
    }
    window.addEventListener('resize', debounce(onResize, 300, { leading: true, trailing: false }));
    window.addEventListener('resize', debounce(onResize, 300, { leading: false, trailing: true }));

    updateViewHeightVariable();

    /* eslint-disable no-new */
    new Vue({
        store,
        i18n,
        el: '#app',
        router,
        render: h => h(App)
    });

    /* eslint-disable no-new */
    new Vue({
        el: '#globalLoadingScreen',
        mounted() {
            store.watch(
                state => store.getters.appIsReady,
                ready => {
                    if (ready) {
                        this.hide();
                    } else {
                        this.show();
                    }
                },
                { immediate: true }
            );
        },
        methods: {
            hide() {
                setTimeout(() => {
                    this.$el.classList.add('hide');
                }, 1000);
                setTimeout(() => {
                    this.$el.style.display = 'none';
                }, 3000);
            },
            show() {
                this.$el.style.display = 'flex';
                this.$el.classList.remove('hide');
            }
        }
    });
}
